import { useState, useCallback } from 'react';
import { message } from 'antd';
import { c212Api, FC210, FC211, FC213 } from '../../../services/c212Api';
import { Mode } from './useC212Mode';

export function useC212Record() {
  const [record, setRecord] = useState<FC210 | null>(null);
  const [formData, setFormData] = useState<Partial<FC210>>({});
  const [details, setDetails] = useState<Partial<FC211>[]>([]);
  const [attachments, setAttachments] = useState<Partial<FC213>[]>([]);
  const [selectedDetailRow, setSelectedDetailRow] = useState(0);
  const [selectedAttachRow, setSelectedAttachRow] = useState(0);
  const [loading, setLoading] = useState(false);

  /** Load a single record by ID */
  const loadRecord = useCallback(async (id: string): Promise<{ data: FC210 } | null> => {
    setLoading(true);
    try {
      const data = await c212Api.get(id);
      setRecord(data);
      setFormData({ ...data });
      setDetails(data.details || []);
      setAttachments(data.attachments || []);
      setSelectedDetailRow(0);
      setSelectedAttachRow(0);
      return { data };
    } catch {
      message.error('載入失敗');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /** Set a single master field */
  const setField = useCallback((key: keyof FC210, value: FC210[keyof FC210]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  /** Get a single master field value */
  const getField = useCallback((key: keyof FC210): FC210[keyof FC210] => {
    return (formData as Record<string, FC210[keyof FC210]>)[key] ?? '';
  }, [formData]);

  /** Save (create or update) [T-F01: use mutation response] */
  const handleSave = useCallback(async (
    mode: Mode,
    onSuccess: (result: FC210) => void,
  ) => {
    setLoading(true);
    try {
      const req = {
        master: formData as FC210,
        details: details as FC211[],
        attachments: attachments as FC213[],
      };
      if (mode === 'add') {
        const result = await c212Api.create(req);
        message.success(`新增成功: ${result.fc210_01}`);
        onSuccess(result);
      } else {
        const result = await c212Api.update(formData.fc210_01!, req);
        message.success('修改成功');
        setRecord(result);
        setFormData({ ...result });
        setDetails(result.details || []);
        setAttachments(result.attachments || []);
        onSuccess(result);
      }
    } catch (e: unknown) {
      // [T-F06: join 422 errors for display]
      const err = e as { response?: { data?: { errors?: { field: string; message: string }[]; error?: string } } };
      const errors = err.response?.data?.errors;
      if (errors && Array.isArray(errors)) {
        const summary = errors.slice(0, 5).map(er => er.message).join('\n');
        const extra = errors.length > 5 ? `\n...共 ${errors.length} 項` : '';
        message.error({ content: summary + extra, duration: 6, style: { whiteSpace: 'pre-line' } });
      } else {
        message.error(err.response?.data?.error || '儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [formData, details, attachments]);

  /** Delete the current record */
  const handleDelete = useCallback(async (): Promise<boolean> => {
    if (!record) return false;
    try {
      await c212Api.delete(record.fc210_01);
      message.success('刪除成功');
      return true;
    } catch {
      message.error('刪除失敗');
      return false;
    }
  }, [record]);

  /** Add a detail row (max 99) */
  const addDetailRow = useCallback(() => {
    const maxSeq = details.reduce((max, d) => Math.max(max, d.fc211_02 || 0), 0);
    if (maxSeq >= 99) {
      message.warning('明細最多 99 項');
      return;
    }
    setDetails(prev => [...prev, {
      fc211_01: formData.fc210_01 || '',
      fc211_02: maxSeq + 1,
      fc211_10: '',
      fc211_11: '',
      fc211_12: 0,
      fc211_13: null,
      fc211_14: '',
    }]);
  }, [details, formData.fc210_01]);

  /** Delete the selected detail row [T-F05: reindex] */
  const deleteDetailRow = useCallback(() => {
    if (details.length <= 0) return;
    setDetails(prev => {
      const next = prev.filter((_, i) => i !== selectedDetailRow);
      return next.map((d, i) => ({ ...d, fc211_02: i + 1 }));
    });
    setSelectedDetailRow(Math.max(0, selectedDetailRow - 1));
  }, [details, selectedDetailRow]);

  /** Add an attachment row (max 99) */
  const addAttachRow = useCallback(() => {
    const maxSeq = attachments.reduce((max, a) => Math.max(max, a.fc213_02 || 0), 0);
    if (maxSeq >= 99) {
      message.warning('附件最多 99 項');
      return;
    }
    setAttachments(prev => [...prev, {
      fc213_01: formData.fc210_01 || '',
      fc213_02: maxSeq + 1,
      fc213_11: '',
      fc213_12: '',
      fc213_21: 0,
      fc213_22: '',
    }]);
  }, [attachments, formData.fc210_01]);

  /** Delete the selected attachment row [T-F05: reindex] */
  const deleteAttachRow = useCallback(() => {
    if (attachments.length <= 0) return;
    setAttachments(prev => {
      const next = prev.filter((_, i) => i !== selectedAttachRow);
      return next.map((a, i) => ({ ...a, fc213_02: i + 1 }));
    });
    setSelectedAttachRow(Math.max(0, selectedAttachRow - 1));
  }, [attachments, selectedAttachRow]);

  /** Reset form to current record (revert) */
  const revertToRecord = useCallback(() => {
    if (record) {
      setFormData({ ...record });
      setDetails(record.details || []);
      setAttachments(record.attachments || []);
    }
  }, [record]);

  /** Reset form for a new add (BR-F06) */
  const resetForAdd = useCallback(() => {
    setRecord(null);
    setFormData({
      fc210_80: 'N',
      fc210_79: '1',
      fc210_17: 0,
      fc210_22: 0,
      fc210_23: 0,
      fc210_25: 0,
      fc210_26: 0,
      fc210_31: 0,
      fc210_33: 0,
    });
    setDetails([]);
    setAttachments([]);
    setSelectedDetailRow(0);
    setSelectedAttachRow(0);
  }, []);

  /** Apply result after save [T-F01] */
  const applyResult = useCallback((result: FC210) => {
    setRecord(result);
    setFormData({ ...result });
    setDetails(result.details || []);
    setAttachments(result.attachments || []);
  }, []);

  return {
    record, formData, details, attachments,
    selectedDetailRow, selectedAttachRow, loading,
    setSelectedDetailRow, setSelectedAttachRow,
    setDetails, setAttachments,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    addDetailRow, deleteDetailRow,
    addAttachRow, deleteAttachRow,
    revertToRecord, resetForAdd, applyResult,
  };
}
